import { CELL } from '../core/config';
import type { Cell } from '../world/city';
import type { Vec } from './structure';

const WIDTH = 8;
const tileKey = (x: number, y: number, z: number) => `${x},${y},${z}`;

/** Local-space broadphase for one authoritative cell map. Cells in a live
 * cluster only leave that map; fracture and reconstruction create new maps.
 * Membership is rechecked so an index never revives a removed cell. Neither
 * arbitrary stable IDs nor rotated body transforms affect this index. */
export class CellRegions {
  private indexes = new WeakMap<ReadonlyMap<number, Cell>, Map<string, Cell[]>>();
  clear(): void {
    this.indexes = new WeakMap();
  }
  *query(cells: ReadonlyMap<number, Cell>, point: Vec, radius: number): Generator<Cell> {
    if (cells.size < 512 || !Number.isFinite(radius)) {
      yield* cells.values();
      return;
    }
    let tiles = this.indexes.get(cells);
    if (!tiles) {
      tiles = new Map();
      for (const cell of cells.values()) {
        // Include the grid centre as well as every actual occupied part. Heat
        // uses physical centres; blast coverage uses occupied box distances.
        let minX = cell.x,
          minY = cell.y,
          minZ = cell.z;
        let maxX = cell.x + 1,
          maxY = cell.y + 1,
          maxZ = cell.z + 1;
        for (const b of cell.parts ?? (cell.bounds ? [cell.bounds] : [])) {
          minX = Math.min(minX, cell.x + b.x);
          minY = Math.min(minY, cell.y + b.y);
          minZ = Math.min(minZ, cell.z + b.z);
          maxX = Math.max(maxX, cell.x + b.x + b.w);
          maxY = Math.max(maxY, cell.y + b.y + b.h);
          maxZ = Math.max(maxZ, cell.z + b.z + b.d);
        }
        for (let y = Math.floor(minY / WIDTH); y <= Math.floor(maxY / WIDTH); y++)
          for (let z = Math.floor(minZ / WIDTH); z <= Math.floor(maxZ / WIDTH); z++)
            for (let x = Math.floor(minX / WIDTH); x <= Math.floor(maxX / WIDTH); x++) {
              const key = tileKey(x, y, z);
              let row = tiles.get(key);
              if (!row) tiles.set(key, (row = []));
              row.push(cell);
            }
      }
      this.indexes.set(cells, tiles);
    }
    const minX = Math.floor((point.x - radius) / CELL / WIDTH),
      maxX = Math.floor((point.x + radius) / CELL / WIDTH),
      minY = Math.floor((point.y - radius) / CELL / WIDTH),
      maxY = Math.floor((point.y + radius) / CELL / WIDTH),
      minZ = Math.floor((point.z - radius) / CELL / WIDTH),
      maxZ = Math.floor((point.z + radius) / CELL / WIDTH);
    // A city-wide query is cheaper as one scan, with the same exact narrowphase.
    if ((maxX - minX + 1) * (maxY - minY + 1) * (maxZ - minZ + 1) > tiles.size * 2) {
      yield* cells.values();
      return;
    }
    const seen = new Set<number>();
    for (let y = minY; y <= maxY; y++)
      for (let z = minZ; z <= maxZ; z++)
        for (let x = minX; x <= maxX; x++)
          for (const cell of tiles.get(tileKey(x, y, z)) ?? []) {
            if (seen.has(cell.id) || cells.get(cell.id) !== cell) continue;
            seen.add(cell.id);
            yield cell;
          }
  }
}
